import { useCallback, useEffect, useRef, useState } from "react";
import {
  FaCircleCheck,
  FaCirclePlus,
  FaCircleXmark,
  FaClock,
  FaMoneyBillWave,
} from "react-icons/fa6";
import { MdOutlineFileDownload } from "react-icons/md";
import { useDispatch } from "react-redux";
import { setActivePage, setShowPreloader } from "../store/activePage";
import CustomModal from "../components/modal";
import PaymentComponent from "../components/payment-gateway";
import handleGetWalletBalance from "../hooks/getWalletBalance";
import handleGetWalletTransaction from "../hooks/getWalletTransaction";
import { AxiosPostMethod } from "../helpers/customAxios";
import HandleLogout from "../hooks/handleLogout";
import { ErrorToast, SuccessToast } from "../components/toastify";
import handleUpdateWalletStatus from "../hooks/updateWalletStatus";
import formatAmount from "../helpers/formatCurrency";
import formatDateTime from "../helpers/formatDateWithTime";
import {
  initiateRedemption,
  getOrgRedemptionList,
} from "../hooks/redemptionApi";

const formatPaymentType = (value) => {
  if (!value) return "N/A";

  return String(value)
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getPaymentTypeBadgeClass = (value) => {
  const normalizedValue = String(value || "").toUpperCase();

  if (normalizedValue.includes("REF")) {
    return "bg-violet-50 text-violet-700 border-violet-200";
  }

  if (normalizedValue.includes("DEBIT")) {
    return "bg-rose-50 text-rose-700 border-rose-200";
  }

  if (normalizedValue.includes("CREDIT")) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }

  return "bg-slate-100 text-slate-700 border-slate-200";
};

const getTransactionStatusBadge = (status) => {
  if (status === "pending") {
    return (
      <span
        title="Payment pending"
        className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700"
      >
        <FaClock />
        Payment Pending
      </span>
    );
  }

  if (status === "awaiting_approval") {
    return (
      <span
        title="Payment received; awaiting admin approval"
        className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-700"
      >
        <FaClock />
        Awaiting Admin Approval
      </span>
    );
  }

  if (status === "rejected") {
    return (
      <span
        title="Wallet credit rejected by admin"
        className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-600"
      >
        <FaCircleXmark />
        Wallet Credit Rejected
      </span>
    );
  }

  if (status === "success") {
    return (
      <span
        title="Payment success"
        className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-600"
      >
        <FaCircleCheck />
        Payment Success
      </span>
    );
  }

  return (
    <span
      title="Payment failed"
      className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-500"
    >
      <FaCircleXmark />
      Payment Failed
    </span>
  );
};

const StatementModal = ({ title, subtitle, isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-white/80 bg-white shadow-2xl shadow-slate-300/70">
        <div className="flex items-start justify-between border-b border-slate-200 bg-[linear-gradient(135deg,#f8fbfd_0%,#eef7f8_48%,#e3f1ff_100%)] px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-teal-700">
              Wallet Statement
            </p>
            <h3 className="mt-1 text-2xl font-black text-slate-900">
              {title}
            </h3>
            {subtitle && (
              <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
          >
            Close
          </button>
        </div>

        <div className="overflow-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
};

const trimValue = (value) => String(value ?? "").trim();

const hasCompleteBankDetails = (form = {}) =>
  Boolean(
    trimValue(form.account_holder_name) &&
      trimValue(form.account_number) &&
      trimValue(form.ifsc_code),
  );

const hasUpiDetails = (form = {}) => Boolean(trimValue(form.upi_id));

const resolvePayoutMethod = (form = {}) => {
  if (hasCompleteBankDetails(form)) return "BANK_TRANSFER";
  if (hasUpiDetails(form)) return "UPI";
  return null;
};

const OperatorWallet = () => {
  const [amount, setAmount] = useState(0);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [lastRecharges, setLastRecharges] = useState([]);
  const [walletTransactionTotal, setWalletTransactionTotal] = useState(0);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    upi_id: "",
    mobile_number: "",
    remarks: "",
    payout_method: "BANK_TRANSFER", // Default value
  });
  const [redemptionList, setRedemptionList] = useState([]);
  const [redemptionTotal, setRedemptionTotal] = useState(0);
  const [allTransactions, setAllTransactions] = useState([]);
  const [allRedemptions, setAllRedemptions] = useState([]);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showAllRedemptions, setShowAllRedemptions] = useState(false);
  const enteredAmount = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Wallet",
        activeMenu: "walletPage",
      }),
    );
  }, [dispatch]);

  const HandleWalletBalance = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const walletBalanceRes = await handleGetWalletBalance();
    dispatch(setShowPreloader(false));
    let balanceValue = walletBalanceRes?.["wallet_balance"];
    if (balanceValue !== undefined && balanceValue !== null) {
      setWalletBalance(balanceValue);
    }
  }, [dispatch]);

  const HandleWalletTransaction = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const walletTransactionRes = await handleGetWalletTransaction();
    dispatch(setShowPreloader(false));
    if (walletTransactionRes) {
      setLastRecharges(walletTransactionRes.transactions || []);
      setWalletTransactionTotal(walletTransactionRes.total || 0);
    }
  }, [dispatch]);

  const fetchRedemptionList = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const data = await getOrgRedemptionList();
    dispatch(setShowPreloader(false));
    if (data) {
      setRedemptionList(data.redemptions || []);
      setRedemptionTotal(data.total || 0);
    }
  }, [dispatch]);

  const openAllTransactions = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const data = await handleGetWalletTransaction(
      walletTransactionTotal > 0 ? walletTransactionTotal : 1000,
      0,
    );
    dispatch(setShowPreloader(false));
    if (data) {
      setAllTransactions(data.transactions || []);
      setShowAllTransactions(true);
    }
  }, [dispatch, walletTransactionTotal]);

  const openAllRedemptions = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const data = await getOrgRedemptionList(
      redemptionTotal > 0 ? redemptionTotal : 1000,
      0,
    );
    dispatch(setShowPreloader(false));
    if (data) {
      setAllRedemptions(data.redemptions || []);
      setShowAllRedemptions(true);
    }
  }, [dispatch, redemptionTotal]);

  useEffect(() => {
    HandleWalletBalance();
    HandleWalletTransaction();
    fetchRedemptionList();
  }, [HandleWalletBalance, HandleWalletTransaction, fetchRedemptionList]);

  const HandlePaymentSuccess = async (res) => {
    const updateWalletSts = await handleUpdateWalletStatus(res, dispatch);
    if (updateWalletSts?.status === 200) {
      SuccessToast("Payment received. Awaiting admin approval.");
      HandleWalletBalance();
      HandleWalletTransaction();
      setAmount(0);
    } else {
      ErrorToast("Payment received, but the recharge request could not be created.");
    }
  };
  const HandlePaymentFailure = () => {
    ErrorToast("Error on payment. Please try again later");
  };

  const handleWalletRecharge = async () => {
    if (amount !== "") {
      dispatch(setShowPreloader(true));
      const orderDetails = await AxiosPostMethod("org/wallet/recharge", {
        amount: amount,
      });
      dispatch(setShowPreloader(false));
      setShowRecharge(false);
      if (orderDetails.status === 200) {
        PaymentComponent(
          orderDetails.data.data,
          HandlePaymentSuccess,
          HandlePaymentFailure,
        );
      } else if (orderDetails.status === 401) HandleLogout();
      else ErrorToast("Error on Payment creation");
    }
  };
  const handleWithdrawInput = (key, value) => {
    setWithdrawForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const transactionSummary = {
    total: walletTransactionTotal || lastRecharges?.length || 0,
    success:
      lastRecharges?.filter((recharge) => recharge?.status === "success")
        ?.length || 0,
    pending:
      lastRecharges?.filter((recharge) =>
        ["pending", "awaiting_approval"].includes(recharge?.status),
      )
        ?.length || 0,
    failed:
      lastRecharges?.filter((recharge) => recharge?.status === "failed")
        ?.length || 0,
  };

  const handleWithdrawSubmit = async () => {
    const amountValue = parseFloat(withdrawForm.amount);
    const payoutMethod = resolvePayoutMethod(withdrawForm);

    if (!withdrawForm.amount || Number.isNaN(amountValue) || amountValue <= 0) {
      ErrorToast("Please enter a valid withdrawal amount.");
      return;
    }

    if (!payoutMethod) {
      ErrorToast("Enter bank details or a UPI ID to continue.");
      return;
    }

    if (payoutMethod === "BANK_TRANSFER" && !hasCompleteBankDetails(withdrawForm)) {
      ErrorToast("Enter account holder name, account number, and IFSC code.");
      return;
    }

    if (payoutMethod === "UPI" && !hasUpiDetails(withdrawForm)) {
      ErrorToast("Enter a UPI ID to continue.");
      return;
    }

    dispatch(setShowPreloader(true));
    try {
      const payload = {
        amount_in_paise: Math.round(amountValue * 100),
        payout_method: payoutMethod,
        currency: "INR", // Default value
        redemption_purpose: "WITHDRAWAL", // Default value
        ...(payoutMethod === "BANK_TRANSFER" && {
          account_number: trimValue(withdrawForm.account_number),
          ifsc_code: trimValue(withdrawForm.ifsc_code).toUpperCase(),
          account_holder_name: trimValue(withdrawForm.account_holder_name),
        }),
        ...(payoutMethod === "UPI" && {
          upi_id: trimValue(withdrawForm.upi_id),
        }),
      };

      const response = await initiateRedemption(payload);
      dispatch(setShowPreloader(false));
      if (response.status === 200 || response.status === 201) {
        SuccessToast(
          "Your request has been submitted. It will take 5 to 7 working days to process.",
        );
        HandleWalletBalance();
        HandleWalletTransaction();
        setShowWithdraw(false); // Close modal
      } else if (response.status === 404) {
        ErrorToast("Low account balance. Please top up your wallet.");
        setShowWithdraw(false); // Close modal
      } else {
        // Show backend error message if available
        const errorMessage = response?.data?.message || response?.data?.error || "Failed to submit withdrawal request.";
        ErrorToast(errorMessage);
      }
    } catch (error) {
      dispatch(setShowPreloader(false));
      ErrorToast("An error occurred. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#edf5f7] via-[#f7fbfc] to-[#edf4f8] p-4 md:p-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-teal-700">
              Wallet Overview
            </p>
            <h1 className="mt-2 text-3xl font-bold text-slate-900 md:text-4xl">
              Manage wallet balance and top-up history
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600 md:text-base">
              Check your current balance, review recent recharges, and quickly
              add funds whenever needed.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowRecharge(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-teal-200/70 transition hover:bg-teal-800"
            >
              <FaCirclePlus size={18} />
              Add Balance
            </button>
            <button
              onClick={() => setShowWithdraw(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-5 py-3 text-sm font-semibold text-sky-800 shadow-sm transition hover:bg-sky-100"
            >
              <FaMoneyBillWave size={18} />
              Withdraw
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-3xl p-6 text-white shadow-xl shadow-slate-300/60" style={{ background: "linear-gradient(135deg, #0f2742 0%, #164e63 48%, #0f766e 100%)" }}>
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-medium text-teal-100/90">
                  Available Balance
                </p>
                <h2 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">
                  {formatAmount(walletBalance)}
                </h2>
                <p className="mt-3 text-sm text-slate-200/85">
                  Top-up instantly to keep your services running without
                  interruption.
                </p>
              </div>

              <div className="grid w-full grid-cols-3 gap-3 text-center md:w-auto md:min-w-[18rem]">
                <div className="min-w-0 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 backdrop-blur-sm">
                  <p className="text-[11px] uppercase tracking-[0.12em] leading-tight text-slate-200/85 break-words">
                    Total
                  </p>
                  <p className="mt-1 text-xl font-bold">
                    {transactionSummary.total}
                  </p>
                </div>
                <div className="min-w-0 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 backdrop-blur-sm">
                  <p className="text-[11px] uppercase tracking-[0.12em] leading-tight text-slate-200/85 break-words">
                    Success
                  </p>
                  <p className="mt-1 text-xl font-bold">
                    {transactionSummary.success}
                  </p>
                </div>
                <div className="min-w-0 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 backdrop-blur-sm">
                  <p className="text-[11px] uppercase tracking-[0.12em] leading-tight text-slate-200/85 break-words">
                    Pending
                  </p>
                  <p className="mt-1 text-xl font-bold">
                    {transactionSummary.pending}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            <div className="rounded-2xl border border-white/60 bg-white/90 p-5 shadow-sm backdrop-blur">
              <p className="text-sm font-medium text-gray-500">
                Recent Transactions
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {transactionSummary.total}
              </p>
            </div>
            <div className="rounded-2xl border border-white/60 bg-white/90 p-5 shadow-sm backdrop-blur">
              <p className="text-sm font-medium text-gray-500">
                Successful Payments
              </p>
              <p className="mt-2 text-3xl font-bold text-emerald-600">
                {transactionSummary.success}
              </p>
            </div>
            <div className="rounded-2xl border border-white/60 bg-white/90 p-5 shadow-sm backdrop-blur">
              <p className="text-sm font-medium text-gray-500">
                Failed Payments
              </p>
              <p className="mt-2 text-3xl font-bold text-red-500">
                {transactionSummary.failed}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-3xl border border-white/70 bg-white/90 shadow-xl shadow-slate-200/60 backdrop-blur">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Recent top-up activity
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Latest wallet transactions with payment type and current status.
              </p>
            </div>

            {lastRecharges?.length > 0 && (
              <button
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 transition hover:bg-teal-100"
                onClick={openAllTransactions}
              >
                <MdOutlineFileDownload size={20} />
                Show All Statement
              </button>
            )}
          </div>

          {!lastRecharges || lastRecharges?.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                <MdOutlineFileDownload size={28} />
              </div>
              <h4 className="mt-4 text-lg font-semibold text-gray-800">
                No transactions yet
              </h4>
              <p className="mt-2 text-sm text-gray-500">
                Your recent recharges will appear here once you add balance.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto px-3 py-4 md:px-6">
              <table className="min-w-full border-separate border-spacing-y-3">
                <thead>
                  <tr>
                    <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Transaction
                    </th>
                    <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Last Updated
                    </th>
                    <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lastRecharges.map((recharge) => (
                    <tr
                      key={recharge.transaction_id}
                      className="rounded-2xl bg-slate-50 shadow-sm"
                    >
                      <td className="rounded-l-2xl px-4 py-4">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatAmount(recharge.amount / 100)}
                        </p>
                        <span
                          className={`mt-2 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getPaymentTypeBadgeClass(recharge.payment_type || recharge.type)}`}
                        >
                          {formatPaymentType(recharge.payment_type || recharge.type)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {formatDateTime(recharge.last_updated_time)}
                      </td>
                      <td className="rounded-r-2xl px-4 py-4">
                        {getTransactionStatusBadge(recharge?.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-8 rounded-3xl border border-white/70 bg-white/90 shadow-xl shadow-slate-200/60 backdrop-blur">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Redemption Requests
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Enter either your bank details or your UPI ID. One payout method
                is enough.
              </p>
            </div>

            {redemptionList?.length > 0 && (
              <button
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 transition hover:bg-teal-100"
                onClick={openAllRedemptions}
              >
                <MdOutlineFileDownload size={20} />
                Show All Statement
              </button>
            )}
          </div>

          {!redemptionList || redemptionList?.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                <MdOutlineFileDownload size={28} />
              </div>
              <h4 className="mt-4 text-lg font-semibold text-gray-800">
                No redemption requests yet
              </h4>
              <p className="mt-2 text-sm text-gray-500">
                Your recent recharges will appear here once you add balance.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto px-3 py-4 md:px-6">
              <table className="min-w-full border-separate border-spacing-y-3">
                <thead>
                  <tr>
                    <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Amount
                    </th>
                    <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Last Updated
                    </th>
                    <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Status
                    </th>
                    <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Failure Reason
                    </th>
                    <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Payout Method
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {redemptionList.map((redemption) => (
                    <tr
                      key={redemption.redemption_id}
                      className="rounded-2xl bg-slate-50 shadow-sm"
                    >
                      <td className="rounded-l-2xl px-4 py-4 text-sm font-semibold text-gray-900">
                        {formatAmount(redemption.amount_in_paise / 100)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {redemption.initiated_at
                          ? formatDateTime(redemption.initiated_at)
                          : "N/A"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {redemption.status || "N/A"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {redemption.failure_reason || "N/A"}
                      </td>
                      <td className="rounded-r-2xl px-4 py-4 text-sm text-gray-500">
                        {redemption.payout_method === "BANK_TRANSFER"
                          ? `${redemption.account_holder_name} (${redemption.account_number})`
                          : redemption.upi_id || "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-white/70 bg-white/90 p-6 shadow-xl">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Redemption History
            </h3>
            <ul className="mt-6 space-y-4">
              {redemptionList.map((item) => (
                <li key={item.redemption_id} className="rounded-lg bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-700">
                    #{item.redemption_id} - {item.status || "N/A"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Amount: {formatAmount(item.amount_in_paise / 100)}
                  </p>
                  <p className="text-xs text-gray-500">
                    Initiated At: {item.initiated_at ? formatDateTime(item.initiated_at) : "N/A"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Purpose: {item.redemption_purpose || "N/A"}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <CustomModal
        bodycontent={
          <div className="px-2 py-4">
            <div className="rounded-2xl bg-slate-50 p-5">
              <p className="text-sm font-medium text-gray-500">
                Enter top-up amount
              </p>
              <div className="mt-4">
                <label
                  htmlFor="wallet-topup-amount"
                  className="mb-2 block text-sm font-semibold text-gray-700"
                >
                  Amount
                </label>
                <input
                  id="wallet-topup-amount"
                  ref={enteredAmount}
                  translate="no"
                  className="no-translate w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                  placeholder="Enter amount"
                  onInput={(e) => {
                    setAmount(e.target.value);
                  }}
                />
                <p className="mt-3 text-sm font-medium text-gray-700">
                  {amount && amount > 0 ? formatAmount(amount) : " "}
                </p>
              </div>

              <button
                onClick={handleWalletRecharge}
                className="mt-5 w-full rounded-xl bg-teal-700 py-3 text-sm font-semibold text-white shadow-lg shadow-teal-200 transition hover:bg-teal-800"
              >
                Pay Now
              </button>
            </div>
          </div>
        }
        showModal={showRecharge}
        closeModal={() => setShowRecharge(false)}
        type="Top-up Form"
      />

      <CustomModal
        bodycontent={
          <div className="px-6 py-6">
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900">Withdraw Funds</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Submit a withdrawal request to transfer funds to your account
                </p>
              </div>

              {/* Amount and Mobile */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="withdraw-amount"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    Withdrawal Amount (₹)
                  </label>
                  <input
                    id="withdraw-amount"
                    type="number"
                    translate="no"
                    className="no-translate w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                    placeholder="Enter amount"
                    value={withdrawForm.amount}
                    onChange={(e) =>
                      handleWithdrawInput("amount", e.target.value)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="withdraw-mobile"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    Mobile Number
                  </label>
                  <input
                    id="withdraw-mobile"
                    type="tel"
                    translate="no"
                    className="no-translate w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                    placeholder="Enter mobile number"
                    value={withdrawForm.mobile_number}
                    onChange={(e) =>
                      handleWithdrawInput("mobile_number", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="space-y-4">
                <div className="text-center">
                  <span className="rounded-full bg-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Choose Payment Method
                  </span>
                </div>

                {/* Bank Transfer Section */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100">
                      <svg className="h-4 w-4 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Bank Transfer</h4>
                      <p className="text-xs text-gray-500">Transfer to your bank account</p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label
                        htmlFor="account-holder-name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Account Holder Name
                      </label>
                      <input
                        id="account-holder-name"
                        translate="no"
                        className="no-translate w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                        placeholder="Enter account holder name"
                        value={withdrawForm.account_holder_name}
                        onChange={(e) =>
                          handleWithdrawInput(
                            "account_holder_name",
                            e.target.value,
                          )
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="account-number"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Account Number
                      </label>
                      <input
                        id="account-number"
                        translate="no"
                        className="no-translate w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                        placeholder="Enter account number"
                        value={withdrawForm.account_number}
                        onChange={(e) =>
                          handleWithdrawInput("account_number", e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label
                        htmlFor="ifsc-code"
                        className="block text-sm font-medium text-gray-700"
                      >
                        IFSC Code
                      </label>
                      <input
                        id="ifsc-code"
                        translate="no"
                        className="no-translate w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                        placeholder="Enter IFSC code"
                        value={withdrawForm.ifsc_code}
                        onChange={(e) =>
                          handleWithdrawInput("ifsc_code", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-4">
                    <div className="h-px w-16 bg-gray-300"></div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">OR</span>
                    <div className="h-px w-16 bg-gray-300"></div>
                  </div>
                </div>

                {/* UPI Section */}
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                      <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">UPI Transfer</h4>
                      <p className="text-xs text-gray-500">Transfer to your UPI ID</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="upi-id"
                      className="block text-sm font-medium text-gray-700"
                    >
                      UPI ID
                    </label>
                    <input
                      id="upi-id"
                      translate="no"
                      className="no-translate w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-200"
                      placeholder="Enter UPI ID (e.g., user@upi)"
                      value={withdrawForm.upi_id}
                      onChange={(e) =>
                        handleWithdrawInput("upi_id", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <label
                  htmlFor="withdraw-remarks"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Remarks (Optional)
                </label>
                <textarea
                  id="withdraw-remarks"
                  rows={3}
                  translate="no"
                  className="no-translate w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                  placeholder="Add any additional notes for this withdrawal request"
                  value={withdrawForm.remarks}
                  onChange={(e) =>
                    handleWithdrawInput("remarks", e.target.value)
                  }
                />
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleWithdrawSubmit}
                className="w-full rounded-lg bg-emerald-600 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Submit Withdrawal Request
              </button>
            </div>
          </div>
        }
        showModal={showWithdraw}
        closeModal={() => setShowWithdraw(false)}
        type="Withdraw Request Form"
      />

      <StatementModal
        title="All Top-up Transactions"
        subtitle={`Showing ${allTransactions.length} of ${walletTransactionTotal} wallet transactions`}
        isOpen={showAllTransactions}
        onClose={() => setShowAllTransactions(false)}
      >
        {!allTransactions.length ? (
          <p className="py-10 text-center text-sm font-medium text-slate-500">
            No wallet transactions found.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-y-3">
              <thead>
                <tr>
                  <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Transaction
                  </th>
                  <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Last Updated
                  </th>
                  <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {allTransactions.map((recharge) => (
                  <tr
                    key={recharge.transaction_id}
                    className="rounded-2xl bg-slate-50 shadow-sm"
                  >
                    <td className="rounded-l-2xl px-4 py-4">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatAmount(recharge.amount / 100)}
                      </p>
                      <span
                        className={`mt-2 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getPaymentTypeBadgeClass(recharge.payment_type || recharge.type)}`}
                      >
                        {formatPaymentType(
                          recharge.payment_type || recharge.type,
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {formatDateTime(recharge.last_updated_time)}
                    </td>
                    <td className="rounded-r-2xl px-4 py-4">
                      {getTransactionStatusBadge(recharge?.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </StatementModal>

      <StatementModal
        title="All Redemption Requests"
        subtitle={`Showing ${allRedemptions.length} of ${redemptionTotal} redemption requests`}
        isOpen={showAllRedemptions}
        onClose={() => setShowAllRedemptions(false)}
      >
        {!allRedemptions.length ? (
          <p className="py-10 text-center text-sm font-medium text-slate-500">
            No redemption requests found.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-y-3">
              <thead>
                <tr>
                  <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Amount
                  </th>
                  <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Last Updated
                  </th>
                  <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Failure Reason
                  </th>
                  <th className="px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Payout Method
                  </th>
                </tr>
              </thead>
              <tbody>
                {allRedemptions.map((redemption) => (
                  <tr
                    key={redemption.redemption_id}
                    className="rounded-2xl bg-slate-50 shadow-sm"
                  >
                    <td className="rounded-l-2xl px-4 py-4 text-sm font-semibold text-gray-900">
                      {formatAmount(redemption.amount_in_paise / 100)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {redemption.initiated_at
                        ? formatDateTime(redemption.initiated_at)
                        : "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {redemption.status || "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {redemption.failure_reason || "N/A"}
                    </td>
                    <td className="rounded-r-2xl px-4 py-4 text-sm text-gray-500">
                      {redemption.payout_method === "BANK_TRANSFER"
                        ? `${redemption.account_holder_name} (${redemption.account_number})`
                        : redemption.upi_id || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </StatementModal>
    </div>
  );
};

export default OperatorWallet;
